import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import CountUp from 'react-countup';
import { CheckCircle2, MapPin } from 'lucide-react';
import { Button } from '../ui/button';
import { Reveal, SectionHeading } from '../SectionHeading';
import { CITIES } from '../../data/siteData';

const counters = [
  { value: 15, suffix: '+', label: 'Cities Served' },
  { value: 500, suffix: '+', label: 'Active Workforce' },
  { value: 120, suffix: '+', label: 'Projects Delivered' },
];

export const IndiaMap = () => {
  const [active, setActive] = useState(CITIES[0]);

  return (
    <section id="cities" data-testid="cities-section" className="py-20 md:py-32 bg-[#0A192F] relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(#4DA3FF 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-center mb-6">
          <span className="px-4 py-1.5 rounded-full border border-[#4DA3FF]/30 bg-[#4DA3FF]/10 text-[#4DA3FF] text-xs font-semibold uppercase tracking-[0.15em]">
            Expanding Across India
          </span>
        </div>
        <SectionHeading dark label="Cities We Operate In" title="WorkLynx Presence Across India"
          subtitle="Hover or tap any city marker to see operational status, workforce availability and services." />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal className="relative max-w-md mx-auto w-full">
            <img src="/india.svg" alt="WorkLynx operational cities across India" className="w-full h-auto opacity-90" loading="lazy" />
            {CITIES.map((c) => (
              <button
                key={c.name}
                data-testid={`city-marker-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
                aria-label={`View ${c.name} details`}
                onMouseEnter={() => setActive(c)}
                onFocus={() => setActive(c)}
                onClick={() => setActive(c)}
                className="absolute -translate-x-1/2 -translate-y-1/2"
                style={{ left: `${c.x}%`, top: `${c.y}%` }}
              >
                <span className={`wl-pulse relative block rounded-full transition-all duration-200 ${
                  active.name === c.name ? 'w-3.5 h-3.5 bg-[#7CC0FF] ring-4 ring-[#4DA3FF]/30' : 'w-2.5 h-2.5 bg-[#4DA3FF]'
                }`} />
              </button>
            ))}
          </Reveal>

          <div>
            <Reveal className="p-7 rounded-xl backdrop-blur-xl bg-white/[0.06] border border-white/15 shadow-[0_8px_32px_rgba(0,0,0,0.25)]" data-testid="city-detail-card">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-md bg-[#4DA3FF]/15 border border-[#4DA3FF]/25 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-[#4DA3FF]" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white font-heading" data-testid="city-detail-name">{active.name}</h3>
                  <div className="flex items-center gap-2 text-xs text-emerald-400 font-medium mt-0.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    Operational · {active.workforce}
                  </div>
                </div>
                {active.hq && <span className="ml-auto text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-[#4DA3FF]/15 text-[#7CC0FF] border border-[#4DA3FF]/25">Office Hub</span>}
              </div>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {active.services.map((s) => (
                  <div key={s} className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" /> {s}
                  </div>
                ))}
              </div>
            </Reveal>

            <div className="grid grid-cols-3 gap-4 mt-8">
              {counters.map((c) => (
                <Reveal key={c.label} className="text-center p-4 rounded-lg bg-white/[0.04] border border-white/10">
                  <div className="text-2xl md:text-3xl font-bold text-white font-heading">
                    <CountUp end={c.value} duration={2} enableScrollSpy scrollSpyOnce />{c.suffix}
                  </div>
                  <div className="text-xs text-slate-400 mt-1 uppercase tracking-wider">{c.label}</div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.15} className="mt-8">
              <Button asChild size="lg" data-testid="cities-cta" className="bg-[#0066CC] hover:bg-[#0052A3] text-white w-full sm:w-auto px-8">
                <Link to="/#contact">Request Workforce Deployment</Link>
              </Button>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
